import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import {
    INIT_SUCCESS,
    LOADING_FLEX_BUY_PAYMENT_GRID,
    LOADING_UPDATE_FLEX_BUY_CASH_DOWN,
    LOADING_UPDATE_FLEX_BUY_TERM_AND_PERCENT,
    PAYMENT_RECEIVED,
    RESET_FLEX_BUY_PAYMENT_GRID_DATA,
    SET_FLEX_BUY_PAYMENT_GRID_PRICED_AT,
    UPDATE_FLEX_BUY_PAYMENT_GRID_AXIS_VALUES,
    UPDATE_FLEX_BUY_PAYMENT_GRID_DATA,
    UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE
} from '../types/actionTypes';
import type { FlexBuyPaymentState } from '../types/flexBuyTypes';
import {
    generateFlexBuyPaymentGridData,
    updateFlexBuyPaymentGridDataByRow,
    updateFlexBuyPaymentGridDataByColumn
} from '../utils/paymentGridUtils';

export const initialState: FlexBuyPaymentState = {
    currentFlexBuyPayment: null,
    flexBuyPaymentGridData: null,
    // Listed explicitly so RESET_FLEX_BUY_PAYMENT_GRID_DATA, which spreads this state, clears it too — a
    // timestamp outliving the grid it dates would claim a cleared grid had freshly priced.
    flexBuyPaymentGridPricedAt: undefined,
    paymentFlexBuyGridToggle: false,
    flexBuyPercents: [],
    flexBuyDownPayments: [],
    flexBuyTerms: []
};

export const reducer = (state = initialState, action: AnyFSA) => {
    switch (action.type) {
        case PAYMENT_RECEIVED: {
            // R-10 latest-call-wins: drop a superseded (stale) response so an out-of-order lease call
            // can't overwrite the fresh flex-buy result. Only leaseEndpointMiddleware sets this.
            if (action.meta?.isStalePaymentResponse) {
                return state;
            }
            const flexBuyPayment: FlexBuyPaymentState['currentFlexBuyPayment'] = action.payload?.finance?.flexBuyPayment;
            const flexBuyPaymentGridData = action.payload?.finance
                ?.flexBuyPaymentGridData as FlexBuyPaymentState['flexBuyPaymentGridData'];

            const newState = { ...state };

            if (flexBuyPayment) {
                newState.currentFlexBuyPayment = flexBuyPayment;
            }

            if (flexBuyPaymentGridData?.length) {
                newState.flexBuyPaymentGridData = flexBuyPaymentGridData;
            }

            return newState;
        }

        case UPDATE_FLEX_BUY_PAYMENT_GRID_DATA: {
            const payload: FlexBuyPaymentState['flexBuyPaymentGridData'] = action.payload;

            if (!payload) return state;

            return { ...state, flexBuyPaymentGridData: payload };
        }

        case LOADING_FLEX_BUY_PAYMENT_GRID: {
            const { flexBuyDownPayments, flexBuyTerms } = action.payload;

            const initFlexBuyPaymentGridData = generateFlexBuyPaymentGridData(flexBuyDownPayments, flexBuyTerms);

            return { ...state, flexBuyPaymentGridData: initFlexBuyPaymentGridData };
        }

        case LOADING_UPDATE_FLEX_BUY_TERM_AND_PERCENT: {
            const { columnIndex } = action.payload;

            const flexBuyPaymentGridData = state!.flexBuyPaymentGridData;

            if (!flexBuyPaymentGridData) return state;

            const initFlexBuyPaymentGridData = updateFlexBuyPaymentGridDataByColumn(flexBuyPaymentGridData, columnIndex);

            return { ...state, flexBuyPaymentGridData: initFlexBuyPaymentGridData };
        }

        case LOADING_UPDATE_FLEX_BUY_CASH_DOWN: {
            const { rowId } = action.payload;

            const flexBuyPaymentGridData = state!.flexBuyPaymentGridData;

            if (!flexBuyPaymentGridData) return state;

            const initFlexBuyPaymentGridData = updateFlexBuyPaymentGridDataByRow(flexBuyPaymentGridData, rowId);

            return { ...state, flexBuyPaymentGridData: initFlexBuyPaymentGridData };
        }

        case UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE: {
            const toggled = action.payload;

            return {
                ...state,
                paymentFlexBuyGridToggle: toggled
            };
        }

        case UPDATE_FLEX_BUY_PAYMENT_GRID_AXIS_VALUES: {
            const { flexBuyPercents, flexBuyDownPayments, flexBuyTerms } = action.payload;

            return {
                ...state,
                flexBuyPercents,
                flexBuyDownPayments,
                flexBuyTerms
            };
        }

        case SET_FLEX_BUY_PAYMENT_GRID_PRICED_AT: {
            return { ...state, flexBuyPaymentGridPricedAt: action.payload };
        }

        case RESET_FLEX_BUY_PAYMENT_GRID_DATA: {
            const { currentFlexBuyPayment: _currentFlexBuyPayment, ...rest } = initialState;

            return {
                ...state,
                ...rest
            };
        }

        case INIT_SUCCESS: {
            const offer = action.payload.offer;
            const offerPayment = offer?.payment;

            if (!offerPayment) return state;

            return {
                ...state,
                currentFlexBuyPayment: {
                    initialPaymentDiscount: offerPayment?.initialPaymentDiscount,
                    comparableTermPaymentDetail: offerPayment?.comparableTermPaymentDetail,
                    firstTermPaymentDetail: offerPayment?.firstTermPaymentDetail,
                    secondTermPaymentDetail: offerPayment?.secondTermPaymentDetail
                }
            };
        }
        default: {
            return state;
        }
    }
};
